// #ux-redesign-start: Tabs primitive (Paso 2 — agregado tardío para Account)
// Tabs verticales u horizontales, controladas por value/onChange.
// La lista de tabs y su contenido se declaran en un solo array de config,
// permitiendo render condicional simple (sólo se monta el panel activo).
//
// PORTABILIDAD: trasplantable a Web-Patients.
//
// USO:
//   <Tabs
//     orientation="vertical"
//     value={active}
//     onChange={setActive}
//     tabs={[
//       { key: 'profile',  label: 'Profil',  icon: <i className="icon-user-active" />, content: <ProfilePanel /> },
//       { key: 'security', label: 'Sicherheit', icon: <i className="icon-shield" />, content: <SecurityPanel />, badge: 2 },
//     ]}
//   />
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import PropTypes from 'prop-types';

const Tabs = ({
  orientation = 'horizontal',
  value,
  onChange,
  tabs,
  ariaLabel,
  className = '',
}) => {
  const active = tabs.find((t) => t.key === value) || tabs[0];

  const handleKeyDown = (e, idx) => {
    const enabled = tabs.filter((t) => !t.disabled);
    const currentIdx = enabled.findIndex((t) => t.key === value);
    const next = orientation === 'vertical' ? 'ArrowDown' : 'ArrowRight';
    const prev = orientation === 'vertical' ? 'ArrowUp' : 'ArrowLeft';

    if (e.key === next) {
      e.preventDefault();
      const t = enabled[(currentIdx + 1) % enabled.length];
      onChange(t.key);
    } else if (e.key === prev) {
      e.preventDefault();
      const t = enabled[(currentIdx - 1 + enabled.length) % enabled.length];
      onChange(t.key);
    } else if (e.key === 'Home') {
      e.preventDefault();
      onChange(enabled[0].key);
    } else if (e.key === 'End') {
      e.preventDefault();
      onChange(enabled[enabled.length - 1].key);
    }
  };

  const classes = [
    'ds-tabs',
    `ds-tabs--${orientation}`,
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={classes}>
      <div
        role="tablist"
        aria-orientation={orientation}
        aria-label={ariaLabel}
        className="ds-tabs__list"
      >
        {tabs.map((t, idx) => {
          const isActive = t.key === active.key;
          return (
            <button
              key={t.key}
              role="tab"
              type="button"
              aria-selected={isActive}
              aria-controls={`ds-tab-panel-${t.key}`}
              id={`ds-tab-${t.key}`}
              tabIndex={isActive ? 0 : -1}
              disabled={t.disabled}
              onClick={() => !t.disabled && onChange(t.key)}
              onKeyDown={(e) => handleKeyDown(e, idx)}
              className={[
                'ds-tabs__tab',
                isActive ? 'ds-tabs__tab--active' : '',
                t.disabled ? 'ds-tabs__tab--disabled' : '',
              ].filter(Boolean).join(' ')}
            >
              {t.icon && <span className="ds-tabs__tab-icon" aria-hidden="true">{t.icon}</span>}
              <span className="ds-tabs__tab-label">{t.label}</span>
              {t.badge !== undefined && t.badge !== null && (
                <span className="ds-tabs__tab-badge">{t.badge}</span>
              )}
            </button>
          );
        })}
      </div>
      {/* #ux-redesign: omitimos el panel cuando el tab activo no trae content
          (uso "bar only" — Tabs como switch que el consumer cablea afuera).
          Esto también elimina el gap del flex column, que de otro modo
          quedaba como espacio muerto debajo del tab list. */}
      {active.content != null && (
        <div
          role="tabpanel"
          id={`ds-tab-panel-${active.key}`}
          aria-labelledby={`ds-tab-${active.key}`}
          className="ds-tabs__panel"
        >
          {active.content}
        </div>
      )}
    </div>
  );
};

Tabs.propTypes = {
  orientation: PropTypes.oneOf(['horizontal', 'vertical']),
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  tabs: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.node.isRequired,
    icon: PropTypes.node,
    badge: PropTypes.node,
    disabled: PropTypes.bool,
    content: PropTypes.node,
  })).isRequired,
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
};

export default Tabs;
// #ux-redesign-end
